import { useFragment, graphql } from 'react-relay';

import { useGetPriceAmountByType } from '../pricingGuidanceHelpers.ts';

import { type useShowPricingGuidance_item$key } from './__generated__/useShowPricingGuidance_item.graphql.ts';

/**
 * TOF-1570. Note: the ML team is working on new price recommendations that will replace the current implementation.
 * The endpoint will have price caps included. Given that we will only be using the existing endpoint for so much longer
 * and that BE is constrained on bandwidth, we’ve opted to add this logic on the FE as opposed to updating the endpoint
 * logic on the BE. When we switch over to the ML model, we should remove this logic.
 */
const priceLimits = {
    USD: 50000,
    EUR: 47500,
    GBP: 41000,
};

const RECOMMENDED_UPPER_PRICE_CAP = 2.5;
const RECOMMENDED_LOWER_PRICE_CAP = 0.4;

const itemFragment = graphql`
    fragment useShowPricingGuidance_item on Item {
        ...pricingGuidanceHelpers_item
    }
`;

export const useShowPricingGuidance = ({
    item: itemRef,
    currency,
}: {
    item: useShowPricingGuidance_item$key | null;
    currency: string;
}): boolean => {
    const item = useFragment(itemFragment, itemRef) || null;

    const itemPrice = useGetPriceAmountByType({ item, currency, type: 'ITEM_PRICE' });
    const median = useGetPriceAmountByType({ item, currency, type: 'MEDIAN' });

    const limit = priceLimits[currency as keyof typeof priceLimits];

    if (!itemPrice) {
        return false;
    }

    if (limit && itemPrice >= limit) {
        return false;
    }

    if (
        median &&
        (median <= itemPrice * RECOMMENDED_LOWER_PRICE_CAP ||
            median >= itemPrice * RECOMMENDED_UPPER_PRICE_CAP)
    ) {
        return false;
    }

    return true;
};
